import { A } from '@adornis/base/env-info.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { from } from 'rxjs';
import { ChancenEventlog } from './ChancenEventlog.js';
import { MailEventWithUser } from './MailEventlog.js';

@Entity()
export class VendorOSStat extends AdornisEntity {
  static override _class = 'VendorOSStat';

  static override resolveID(entity): string {
    return A.getGloballyUniqueID();
  }

  @Field(type => String) os?: string;
  @Field(type => String) vendor?: string;
  @Field(type => Int, { default: v => v ?? 0 }) users!: number;
}

@Entity()
export class PageLoadEventLog extends ChancenEventlog {
  static override _class = 'PageLoadEventLog';

  @Query(type => [VendorOSStat])
  static getVendorOSStats(@Arg('limit', type => Int) limit?: number) {
    return async (gqlFields: BaseQLSelectionSet<VendorOSStat>) => {
      const col = await getRawCollection(this._collectionName);
      const results = await col
        .aggregate([
          { $match: { _class: this._class } },
          { $sort: { createdAt: 1 } },
          { $group: { _id: '$clientSession', vendor: { $last: '$vendor' }, platform: { $last: '$platform' } } },
          { $group: { _id: { vendor: '$vendor', platform: '$platform' }, count: { $sum: 1 } } },
          { $project: { _id: 0, vendor: '$_id.vendor', os: '$_id.platform', users: '$count' } },
          { $sort: { count: -1 } },
          ...(limit ? [{ $limit: limit }] : []),
        ])
        .toArray();

      return results.map(resultEntry => new VendorOSStat(resultEntry));
    };
  }

  // we can do this because this event is only thrown if the user logged in on page load
  @Query(type => Int)
  static getAtLeastOneAuthenticatedSession() {
    return async () => {
      const col = await getRawCollection(this._collectionName);
      const result = await col
        .aggregate([{ $match: { _class: this._class } }, { $group: { _id: '$userID' } }, { $count: 'count' }])
        .toArray();

      return result[0]!.count;
    };
  }

  // we can do this because this event is only thrown if the user logged in on page load
  @Query(type => Int)
  static getMultipleAuthenticatedSessions() {
    return async () => {
      const col = await getRawCollection(this._collectionName);
      const result = await col
        .aggregate([
          { $match: { _class: this._class } },
          { $group: { _id: '$userID', sum: { $sum: 1 } } },
          { $match: { sum: { $gt: 1 } } },
          { $count: 'count' },
        ])
        .toArray();

      return result[0]!.count;
    };
  }

  // we can do this because this event is only thrown if the user logged in on page load
  @Subscription(type => [MailEventWithUser])
  static subscribeUsersWithAuthenticatedSessions(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
  ) {
    return (gqlFields: BaseQLSelectionSet<[MailEventWithUser]>) => {
      return from(
        new Promise(async resolve => {
          const { ChancenwerkUser } = await import(
            '@chancenwerk/master-data-management/users/entities/ChancenwerkUser'
          );
          const col = await getRawCollection(this._collectionName);
          const result = await col
            .aggregate([
              { $match: { _class: this._class } },
              { $group: { _id: '$userID', sum: { $sum: 1 } } },
              {
                $lookup: { from: ChancenwerkUser._collectionName, as: 'user', localField: '_id', foreignField: '_id' },
              },
              { $unwind: '$user' },
              { $skip: skip },
              { $limit: limit },
              { $project: { _id: 1, 'user.vorname': 1, 'user.nachname': 1, sum: 1 } },
            ])
            .toArray();

          resolve(
            result.map(
              resultItem =>
                new MailEventWithUser({
                  vorname: resultItem.user.vorname,
                  nachname: resultItem.user.nachname,
                  userID: resultItem._id,
                  info: resultItem.sum,
                }),
            ),
          );
        }),
      );
    };
  }
}
